import styled from "styled-components";

export const DocumentViewerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme.spacing.large};
  animation: fadeIn 0.2s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: 0;
    align-items: stretch;
  }
`;

export const DocumentViewerContainer = styled.div`
  width: 100%;
  max-width: 95vw;
  height: 100%;
  max-height: 95vh;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.primary};
  border-radius: ${(props) => props.theme.box.borderRadius};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    max-width: 100vw;
    max-height: 100vh;
    height: 100vh;
    border-radius: 0;
    box-shadow: none;
  }
`;

export const DocumentViewerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${(props) => props.theme.spacing.large};
  border-bottom: 1px solid ${(props) => props.theme.colors.border};
  background-color: ${(props) => props.theme.colors.secondary};
  flex-shrink: 0;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.medium};
  }
`;

export const DocumentViewerTitle = styled.h2`
  font-size: ${(props) => props.theme.fontSizes.large};
  font-weight: 600;
  color: ${(props) => props.theme.colors.font.primary};
  margin: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: ${(props) => props.theme.spacing.medium};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: ${(props) => props.theme.fontSizes.medium};
    padding-right: ${(props) => props.theme.spacing.small};
  }
`;

export const DocumentViewerCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background-color: transparent;
  color: ${(props) => props.theme.colors.font.primary};
  cursor: pointer;
  border-radius: ${(props) => props.theme.box.borderRadius};
  transition: background-color 0.2s;
  flex-shrink: 0;

  &:hover {
    background-color: ${(props) => props.theme.colors.border};
  }

  svg {
    font-size: ${(props) => props.theme.fontSizes.medium};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;

    svg {
      font-size: 1.25rem;
    }
  }
`;

export const DocumentViewerContent = styled.div`
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: ${(props) => props.theme.colors.primary};
  position: relative;
  min-height: 500px;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    min-height: 0;
    align-items: flex-start;
    padding-top: ${(props) => props.theme.spacing.small};
  }
`;

export const PDFViewerWrapper = styled.div`
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow-y: auto;
  padding: ${(props) => props.theme.spacing.large};
  background-color: ${(props) => props.theme.colors.secondary};
  -webkit-overflow-scrolling: touch;
  min-height: 0;

  .react-pdf__Document {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 100%;
  }

  .react-pdf__Page {
    margin-bottom: ${(props) => props.theme.spacing.medium};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: white;
    max-width: 100%;
  }

  .react-pdf__Page__canvas {
    display: block;
    max-width: 100%;
    height: auto;
  }

  .react-pdf__Page__textContent {
    max-width: 100%;
  }

  .react-pdf__Page__annotations {
    max-width: 100%;
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.small};

    .react-pdf__Page {
      margin-bottom: ${(props) => props.theme.spacing.small};
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    }
  }
`;

export const PDFControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing.large};
  padding: ${(props) => props.theme.spacing.medium};
  background-color: ${(props) => props.theme.colors.primary};
  border-top: 1px solid ${(props) => props.theme.colors.border};
  width: 100%;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 10;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    gap: ${(props) => props.theme.spacing.small};
    padding: ${(props) => props.theme.spacing.small};
    flex-wrap: wrap;
  }
`;

export const PDFPageInfo = styled.span`
  font-size: ${(props) => props.theme.fontSizes.medium};
  color: ${(props) => props.theme.colors.font.primary};
  font-weight: 500;
  white-space: nowrap;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    font-size: ${(props) => props.theme.fontSizes.small};
    width: 100%;
    text-align: center;
    order: -1;
    margin-bottom: ${(props) => props.theme.spacing.small};
  }
`;

export const PDFNavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing.small};
  padding: ${(props) => props.theme.spacing.small} ${(props) => props.theme.spacing.medium};
  border: 1px solid ${(props) => props.theme.colors.border};
  background-color: ${(props) => props.theme.colors.secondary};
  color: ${(props) => props.theme.colors.font.primary};
  font-size: ${(props) => props.theme.fontSizes.small};
  font-weight: 500;
  cursor: pointer;
  border-radius: ${(props) => props.theme.box.borderRadius};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: ${(props) => props.theme.colors.accent1};
    color: ${(props) => props.theme.colors.font.secondary};
    border-color: ${(props) => props.theme.colors.accent1};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: ${(props) => props.theme.fontSizes.medium};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    min-height: 44px;
    min-width: 44px;
    padding: ${(props) => props.theme.spacing.small} ${(props) => props.theme.spacing.medium};
    font-size: ${(props) => props.theme.fontSizes.small};
    flex: 1;
    max-width: 120px;

    svg {
      font-size: 1.125rem;
    }
  }
`;

export const ImageViewer = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  display: block;

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    width: 100%;
    padding: ${(props) => props.theme.spacing.small};
  }
`;

export const DocumentViewerActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: ${(props) => props.theme.spacing.large};
  border-top: 1px solid ${(props) => props.theme.colors.border};
  background-color: ${(props) => props.theme.colors.secondary};
  flex-shrink: 0;
  gap: ${(props) => props.theme.spacing.medium};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.medium};
    justify-content: center;
  }
`;

export const DocumentViewerDownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing.small};
  padding: ${(props) => props.theme.spacing.medium} ${(props) => props.theme.spacing.large};
  border: none;
  background-color: ${(props) => props.theme.colors.accent1};
  color: ${(props) => props.theme.colors.font.secondary};
  font-size: ${(props) => props.theme.fontSizes.medium};
  font-weight: 500;
  cursor: pointer;
  border-radius: ${(props) => props.theme.box.borderRadius};
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }

  svg {
    font-size: ${(props) => props.theme.fontSizes.medium};
  }

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    min-height: 44px;
    width: 100%;
    max-width: 280px;
    font-size: ${(props) => props.theme.fontSizes.medium};
    padding: 12px 24px;
  }
`;

export const DocumentViewerUnsupported = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${(props) => props.theme.spacing.xxlarge};
  text-align: center;
  gap: ${(props) => props.theme.spacing.large};
`;

export const DocumentViewerUnsupportedIcon = styled.div`
  color: ${(props) => props.theme.colors.font.tertiary};
  margin-bottom: ${(props) => props.theme.spacing.medium};
`;

export const DocumentViewerUnsupportedText = styled.p`
  font-size: ${(props) => props.theme.fontSizes.medium};
  color: ${(props) => props.theme.colors.font.primary};
  margin: 0;
  max-width: 400px;
`;

export const DocumentViewerUnsupportedButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing.small};
  padding: ${(props) => props.theme.spacing.medium} ${(props) => props.theme.spacing.large};
  border: none;
  background-color: ${(props) => props.theme.colors.accent1};
  color: ${(props) => props.theme.colors.font.secondary};
  font-size: ${(props) => props.theme.fontSizes.medium};
  font-weight: 500;
  cursor: pointer;
  border-radius: ${(props) => props.theme.box.borderRadius};
  transition: opacity 0.2s;
  margin-top: ${(props) => props.theme.spacing.medium};

  &:hover {
    opacity: 0.8;
  }

  svg {
    font-size: ${(props) => props.theme.fontSizes.medium};
  }
`;




